import {
    Box,
    Checkbox,
    HStack,
    Link,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
    type TableCellProps,
    type TableColumnHeaderProps,
} from '@chakra-ui/react';
import { Fragment, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import MfgPartNumberWithCopy from '../../../components/inventory/MfgPartNumberWithCopy';
import PurchaseOrderPdfButton from '../../../components/inventory/PurchaseOrderPdfButton';
import { formatPrice } from '../../../functions';
import { Icon, IconButton, InsetTable, StatusBadge, Tooltip } from '../../../ui';
import {
    deliveryDaysLabel,
    PurchaseOrderEntry,
    PurchaseOrderStatus,
} from '../../../types/inventory/purchaseOrders';
import {
    formatGrossMargin,
    purchaseOrderEtaDays,
    purchaseOrderPartCount,
    purchaseOrderValue,
} from './purchaseOrderMath';
import { EtaCell, PartActionCell, type PurchaseOrderPartAction } from './PurchaseOrderPartCells';

/** Header cell look shared with InventoryListTable, with a taller row. */
export const PO_TH_PROPS: TableColumnHeaderProps = {
    color: 'gray.500',
    fontSize: '11px',
    fontWeight: 'semibold',
    letterSpacing: 'wider',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    py: 2.5,
    px: 3,
};

const TD_PROPS: TableCellProps = { py: 3, px: 3 };

/** Chevron + select-all column, ahead of RO so the RO label sits over RO values. */
export const PO_CONTROL_CELL_PROPS: TableCellProps = { pl: 6, pr: 0, w: '1%' };

export const PO_MAIN_COLUMN_COUNT = 11;

/** Match Orders feed hover/select checkboxes (OrderHoverCheckbox). */
const PO_CHECKBOX_SX = {
    '& .chakra-checkbox__control': {
        width: '14px',
        height: '14px',
    },
    '& .chakra-checkbox__icon': {
        fontSize: '9px',
    },
} as const;

const STATUS_BADGE: Record<PurchaseOrderStatus, { tone: 'warning' | 'success'; label: string }> = {
    pending: { tone: 'warning', label: 'Pending' },
    completed: { tone: 'success', label: 'Completed' },
};

const stopRowToggle = (e: MouseEvent) => e.stopPropagation();

function TimeOrderedCell({ timePlaced }: { timePlaced: string }) {
    const date = new Date(timePlaced);
    if (Number.isNaN(date.getTime())) return <Text>—</Text>;
    return (
        <VStack spacing={0} align="flex-start">
            <Text fontWeight="bold">
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
            <Text fontSize="xs" color="fg.muted">
                {date.toLocaleDateString()}
            </Text>
        </VStack>
    );
}

export interface PurchaseOrderRowProps {
    request: PurchaseOrderEntry;
    initiallyExpanded: boolean;
    highlight: boolean;
    /** Procurement (`full-time`) orgs can open the linked order. */
    canOpenOrder: boolean;
    dirtyActions: Record<string, PurchaseOrderPartAction>;
    daysDrafts: Record<string, number>;
    selectedPartIds: Set<string>;
    onSetPartAction: (partId: string, action: PurchaseOrderPartAction | undefined) => void;
    onSetDays: (partId: string, days: number) => void;
    onTogglePartSelected: (partId: string, checked: boolean) => void;
    onToggleSelectAllPending: (checked: boolean) => void;
}

export const PurchaseOrderRow = ({
    request,
    initiallyExpanded,
    highlight,
    canOpenOrder,
    dirtyActions,
    daysDrafts,
    selectedPartIds,
    onSetPartAction,
    onSetDays,
    onTogglePartSelected,
    onToggleSelectAllPending,
}: PurchaseOrderRowProps) => {
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const rowRef = useRef<HTMLTableRowElement>(null);

    useEffect(() => {
        if (highlight && rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [highlight]);

    const pendingParts = useMemo(
        () => request.parts.filter((part) => part.status === 'pending'),
        [request.parts],
    );

    const allPendingSelected =
        pendingParts.length > 0 && pendingParts.every((p) => selectedPartIds.has(p.partId));
    const somePendingSelected = pendingParts.some((p) => selectedPartIds.has(p.partId));

    const etaDays = purchaseOrderEtaDays(request.parts, daysDrafts);
    const fulfillerEmail = request.fulfillerEmail ?? request.confirmedByEmail ?? null;
    const fulfillerName = request.fulfillerName?.trim() || null;
    const vehicle = request.vehicle?.trim() || null;
    const status = STATUS_BADGE[request.status] ?? STATUS_BADGE.pending;
    const showPdf = !!(request.purchaseOrderId && request.invoiceNumber);
    const showOpenOrder = canOpenOrder && !!request.orderId;

    const toggleExpanded = () => setExpanded((prev) => !prev);
    const rowBg = highlight ? 'brand.subtle' : expanded ? 'gray.50' : undefined;

    return (
        <Fragment>
            <Tr
                ref={rowRef}
                bg={rowBg}
                cursor="pointer"
                aria-expanded={expanded}
                onClick={toggleExpanded}
                _hover={{ bg: highlight ? 'brand.subtle' : 'gray.50' }}
            >
                <Td {...TD_PROPS} {...PO_CONTROL_CELL_PROPS}>
                    <HStack spacing={2}>
                        <IconButton
                            aria-label={expanded ? 'Collapse purchase order' : 'Expand purchase order'}
                            icon={<Icon name={expanded ? 'chevronDown' : 'chevronRight'} size="md" />}
                            size="xs"
                            variant="ghostGray"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded();
                            }}
                        />
                        {pendingParts.length > 0 ? (
                            <Box as="span" display="inline-flex" onClick={stopRowToggle}>
                                <Checkbox
                                    size="sm"
                                    colorScheme="brand"
                                    sx={PO_CHECKBOX_SX}
                                    isChecked={allPendingSelected}
                                    isIndeterminate={somePendingSelected && !allPendingSelected}
                                    onChange={(e) => onToggleSelectAllPending(e.target.checked)}
                                    aria-label="Select all pending parts for this purchase order"
                                />
                            </Box>
                        ) : (
                            <Box w="14px" flexShrink={0} />
                        )}
                    </HStack>
                </Td>
                <Td {...TD_PROPS}>
                    <VStack spacing={0} align="flex-start" minW={0}>
                        <Text fontWeight="semibold" whiteSpace="nowrap">
                            {request.roNumber}
                        </Text>
                        {request.invoiceNumber ? (
                            <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                                PO {request.invoiceNumber}
                            </Text>
                        ) : null}
                    </VStack>
                </Td>
                <Td {...TD_PROPS}>
                    <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                </Td>
                <Td {...TD_PROPS}>
                    <VStack spacing={0} align="flex-start" minW={0}>
                        <Text noOfLines={1} title={fulfillerName ?? undefined}>
                            {fulfillerName ?? '—'}
                        </Text>
                        {fulfillerEmail ? (
                            <Text fontSize="xs" color="fg.muted" noOfLines={1} title={fulfillerEmail}>
                                {fulfillerEmail}
                            </Text>
                        ) : null}
                    </VStack>
                </Td>
                <Td {...TD_PROPS}>
                    <Text noOfLines={1} title={vehicle ?? undefined}>
                        {vehicle ?? '—'}
                    </Text>
                </Td>
                <Td {...TD_PROPS}>
                    <Text
                        noOfLines={1}
                        title={`${request.sourceLocationName} → ${request.destinationLocationName}`}
                    >
                        {request.sourceLocationName} → {request.destinationLocationName}
                    </Text>
                </Td>
                <Td {...TD_PROPS}>
                    <TimeOrderedCell timePlaced={request.timePlaced} />
                </Td>
                <Td {...TD_PROPS}>{purchaseOrderPartCount(request.parts)}</Td>
                <Td {...TD_PROPS} whiteSpace="nowrap">
                    {formatPrice(purchaseOrderValue(request.parts))}
                </Td>
                <Td {...TD_PROPS} whiteSpace="nowrap">
                    {etaDays === null ? '—' : deliveryDaysLabel(etaDays)}
                </Td>
                <Td {...TD_PROPS} pr={6}>
                    {showPdf || showOpenOrder ? (
                        <HStack spacing={1} w="fit-content" onClick={stopRowToggle}>
                            {showPdf ? (
                                <PurchaseOrderPdfButton
                                    purchaseOrderId={request.purchaseOrderId}
                                    invoiceNumber={request.invoiceNumber as string}
                                    variant="icon"
                                />
                            ) : null}
                            {showOpenOrder ? (
                                <Tooltip label="Open order" placement="top" hasArrow>
                                    <Link
                                        as={RouterLink}
                                        to={`/order/${request.orderId}`}
                                        state={{ source: 'dashboard' }}
                                        aria-label="Open order"
                                        display="inline-flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        boxSize={6}
                                        borderRadius="md"
                                        color="fg.muted"
                                        _hover={{ bg: 'bg.subtle', color: 'fg.default' }}
                                    >
                                        <Icon name="externalLink" size="sm" />
                                    </Link>
                                </Tooltip>
                            ) : null}
                        </HStack>
                    ) : (
                        <Text color="fg.muted">—</Text>
                    )}
                </Td>
            </Tr>
            {expanded ? (
                <Tr>
                    <Td colSpan={PO_MAIN_COLUMN_COUNT} p={0} bg="white">
                        <Box pl={14} pr={6} pt={2} pb={4}>
                            <InsetTable>
                                <Thead>
                                    <Tr>
                                        <Th whiteSpace="nowrap">Part Name</Th>
                                        <Th whiteSpace="nowrap">Mfg. Part Number</Th>
                                        <Th whiteSpace="nowrap">Qty</Th>
                                        <Th whiteSpace="nowrap">Delivery ETA</Th>
                                        <Th whiteSpace="nowrap">List Price</Th>
                                        <Th whiteSpace="nowrap">Buy Price</Th>
                                        <Th whiteSpace="nowrap">Gross Margin</Th>
                                        <Th whiteSpace="nowrap">Action</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {request.parts.map((part) => (
                                        <Tr key={part.partId}>
                                            <Td>
                                                <HStack spacing={2} minW={0}>
                                                    {part.status === 'pending' ? (
                                                        <Checkbox
                                                            size="sm"
                                                            colorScheme="brand"
                                                            sx={PO_CHECKBOX_SX}
                                                            isChecked={selectedPartIds.has(part.partId)}
                                                            onChange={(e) =>
                                                                onTogglePartSelected(
                                                                    part.partId,
                                                                    e.target.checked,
                                                                )
                                                            }
                                                            aria-label={`Select ${part.name}`}
                                                        />
                                                    ) : (
                                                        <Box w="14px" flexShrink={0} />
                                                    )}
                                                    <Text fontWeight="medium" noOfLines={1} title={part.name}>
                                                        {part.name}
                                                    </Text>
                                                </HStack>
                                            </Td>
                                            <Td>
                                                <MfgPartNumberWithCopy
                                                    partNumber={part.partNumber}
                                                    color="fg.default"
                                                    fontWeight="normal"
                                                />
                                            </Td>
                                            <Td>{part.quantity}</Td>
                                            <Td>
                                                <EtaCell
                                                    value={daysDrafts[part.partId] ?? part.deliveryDays}
                                                    editable={part.status === 'pending'}
                                                    onChange={(days) => onSetDays(part.partId, days)}
                                                />
                                            </Td>
                                            <Td whiteSpace="nowrap">{formatPrice(part.listPrice)}</Td>
                                            <Td whiteSpace="nowrap">{formatPrice(part.buyPrice)}</Td>
                                            <Td whiteSpace="nowrap">
                                                {formatGrossMargin(part.listPrice, part.buyPrice)}
                                            </Td>
                                            <Td>
                                                <PartActionCell
                                                    part={part}
                                                    dirty={dirtyActions[part.partId]}
                                                    onChange={(action) =>
                                                        onSetPartAction(part.partId, action)
                                                    }
                                                />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </InsetTable>
                        </Box>
                    </Td>
                </Tr>
            ) : null}
        </Fragment>
    );
};
